import { defineStore } from 'pinia';
import { watch } from 'vue';
import { client, getErrorMessage } from '@/api';
import { ElMessage } from 'element-plus';
import {
  CollectionDTO, CollectionGameModification,
  PaginatedGameCollectionsResponse,
  SortDirectionDto,
  UpdateGameCollectionRequest,
} from '@/api/models';
import { CollectionSortColumn } from '@/open-api/models';
import { debounce } from 'es-toolkit';
import { useCollectionGames } from "@/store/collection-games";

export type CollectionsState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;
  search: string | null;
  categoryName: string | null;
  sortBy: CollectionSortColumn | null;
  sortDirection: SortDirectionDto | null;

  collections: CollectionDTO[];

  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
};

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 15,
  total: 0,
  pageCount: 0,
};

const COLLECTION_SORT_COLUMN_BY_PROP: Record<string, CollectionSortColumn | undefined> = {
  id: CollectionSortColumn.Id,
  name: CollectionSortColumn.Name,
  categoryName: CollectionSortColumn.Category,
  collectionSourceName: CollectionSortColumn.CollectionSource,
  createdAt: CollectionSortColumn.CreatedAt,
  updatedAt: CollectionSortColumn.UpdatedAt,
};

const getInitState = (): CollectionsState => ({
  isWatching: false,
  loading: false,
  error: null,
  search: null,
  categoryName: null,
  sortBy: CollectionSortColumn.UpdatedAt,
  sortDirection: 'DESC',
  collections: [],
  pagination: { ...DEFAULT_PAGINATION },
});

export const useCollectionsStore = defineStore('collections', {
  state: (): CollectionsState => getInitState(),

  actions: {
    init() {
      if (this.isWatching) {
        return;
      }

      this.isWatching = true;

      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.listCollections();
        }
      }, 300);

      watch(
        () => [this.search, this.sortBy, this.sortDirection],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
      this.init();
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    async deleteCollection(collectionId: string):Promise<void> {
      this.setLoading(true);

      try {
        await client.collectionsApi.deleteCollection(collectionId);
      } finally {
        this.setLoading(false);
      }
    },

    setError(value: string | null) {
      this.error = value;
    },

    setSearch(value: string | null) {
      this.search = value;
      this.pagination.page = 1;
    },

    async duplicateCollection(
      name: string,
      collectionToDuplicate: CollectionDTO,
    ) {
      const collectionGamesStore = useCollectionGames();
      const sources = await collectionGamesStore.listCollectionSources();
      const source = sources.find((s) => s.name === collectionToDuplicate.collectionSourceName);
      if (!source) {
        throw new Error(`Collection creation is failed, source '${collectionToDuplicate.collectionSourceName}' is not found`);
      }

      await collectionGamesStore.initEditingGames(collectionToDuplicate.id);
      const games: Array<CollectionGameModification> = collectionGamesStore.collectionGames
        ? collectionGamesStore.collectionGames.map((g) => ({
          gameSlug: g.gameSlug,
          position: g.position,
          modificationType: 'INSERT',
          isManual: g.isManual,
        } as CollectionGameModification))
        : [];

      const duplicatedCollection = await client.collectionsApi.createCollection(
        {
          collectionSourceId: source.id,
          name,
          params: collectionToDuplicate.params,
          games,
        },
      );

      if (!duplicatedCollection) {
        throw new Error(`Collection duplication is failed`);
      }
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        pageSize: limit,
      };
      this.listCollections();
    },

    setSort({ prop, order }: { prop: string; order: string | null }): void {
      if (order === null) {
        this.sortBy = null;
        this.sortDirection = null;
        return;
      }

      const sortBy = COLLECTION_SORT_COLUMN_BY_PROP[prop];

      if (!sortBy) {
        this.sortBy = null;
        this.sortDirection = null;
        return;
      }

      this.sortBy = sortBy;
      this.sortDirection = order === 'ascending' ? 'ASC' : 'DESC';
    },

    async listCollections(): Promise<PaginatedGameCollectionsResponse> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedGameCollectionsResponse = {
        items: [],
        total: 0,
        page: 1,
        pageSize: 15,
        pageCount: 0,
      };

      try {
        data = await client.collectionsApi.listCollections({
          pageNumber: this.pagination.page,
          pageSize: this.pagination.pageSize,
          name: this.search && this.search.length >= 3 ? this.search : undefined,
          categoryName: this.categoryName && this.categoryName.length >= 3 ? this.categoryName : undefined,
          pageSortBy: this.sortBy ?? undefined,
          pageSortDirection: this.sortDirection ? this.sortDirection : undefined,
        });

        const { items = [], total, pageCount } = data;
        this.collections = items;
        this.pagination = {
          ...this.pagination,
          total,
          pageCount,
        };
      } catch (err) {
        const message = getErrorMessage(err, 'Error loading collections');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getCollection(collectionId: string): Promise<CollectionDTO | null> {
      this.setError(null);
      this.setLoading(true);

      let collection: CollectionDTO | null = null;

      try {
        collection = await client.collectionsApi.getCollection(collectionId);
      } catch (err) {
        const message = getErrorMessage(err, 'Error loading collection');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return collection;
    },

    async updateCollection(
      collectionId: string,
      data: UpdateGameCollectionRequest,
    ): Promise<CollectionDTO | null> {
      this.setError(null);
      this.setLoading(true);

      let collection: CollectionDTO | null = null;

      try {
        collection = await client.collectionsApi.updateCollection(collectionId, data);
        ElMessage.success('Collection updated successfully');
        await this.listCollections();
      } catch (err) {
        const message = getErrorMessage(err, 'Error updating collection');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return collection;
    },

    async createCollection(data: {
      collectionSourceId: string;
      name: string;
      params?: Record<string, unknown>;
      games: Array<CollectionGameModification>;
    }): Promise<CollectionDTO | null> {
      this.setError(null);
      this.setLoading(true);

      let collection: CollectionDTO | null = null;

      try {
        collection = await client.collectionsApi.createCollection(data);
        ElMessage.success('Collection created successfully');
        await this.listCollections();
      } catch (err) {
        if ((err as Error).message === 'Server error') {
          ElMessage.error('Collection is not created. Name must be unique');
        } else {
          const message = getErrorMessage(err, 'Error creating collection');
          ElMessage.error(message);
          this.setError(message);
        }
      } finally {
        this.setLoading(false);
      }

      return collection;
    },
  },
});
